import type { ZwChapterNumbering } from "../lib/zwConfig";
/**
 * Pure TEA update for project settings: dialog handler registry and every
 * "build the next `.zwconfig` and persist it" decision.
 *
 * Shell: `useProjectSettingsStore.ts` (Pinia). The shell reads the current
 * config from the session and passes it in each Msg; `update` never reads
 * other stores. Persisting (optimistic write + rollback) is a Cmd.
 */
import type { ProjectSettingsSectionId } from "../components/ProjectSettingsDialog.vue";
import { assertNever } from "../lib/assertNever";
import {
  isJust,
  isNothing,
  just,
  nothing,
  toMaybe,
  type Maybe,
} from "../lib/maybe";
import { goalTarget } from "../lib/writingGoals";
import {
  defaultZwGoals,
  flattenChapters,
  normalizeZwGoals,
  replaceChapter,
  type ManuscriptItem,
  type ZwConfig,
  type ZwDatabaseFieldDef,
  type ZwDatabaseRelationPair,
  type ZwFonts,
  type ZwGoals,
  type ZwMetadata,
  type ZwCopyrightPage,
  type ZwDedicationPage,
  type ZwChapterHeadingStyle,
  type ZwPageChrome,
  type ZwParagraphFormatting,
  type ZwPrintSize,
  type ZwScene,
  type ZwSceneFormatting,
  type ZwStatusBar,
} from "../lib/zwConfig";

export type ProjectSettingsSavePayload = {
  fonts: ZwFonts;
  printSize: ZwPrintSize;
  paragraphFormatting: ZwParagraphFormatting;
  chapterHeadingStyle: ZwChapterHeadingStyle;
  chapterNumbering: ZwChapterNumbering;
  actNumbering: ZwChapterNumbering;
  pageChrome: ZwPageChrome;
  sceneFormatting: ZwSceneFormatting;
  copyrightPage: ZwCopyrightPage;
  dedicationPage: ZwDedicationPage;
  metadata: ZwMetadata;
  dictionary: string[];
  statusBar: ZwStatusBar;
  goals: ZwGoals;
  databaseFields: ZwDatabaseFieldDef[];
  databaseRelationPairs: ZwDatabaseRelationPair[];
};

export type OpenSettingsFn = (section?: ProjectSettingsSectionId) => void;
export type ApplyFontsFn = (fonts: ZwFonts) => void;
export type OnSettingsSavedFn = () => void;

export type ProjectSettingsModel = {
  readonly openSettings: Maybe<OpenSettingsFn>;
  readonly applyFonts: Maybe<ApplyFontsFn>;
  readonly onSettingsSaved: Maybe<OnSettingsSavedFn>;
};

export const initialProjectSettingsModel: ProjectSettingsModel = {
  openSettings: nothing,
  applyFonts: nothing,
  onSettingsSaved: nothing,
};

export type ProjectSettingsMsg =
  | { readonly type: "RegisterOpenSettings"; readonly fn: OpenSettingsFn }
  | { readonly type: "RegisterApplyFonts"; readonly fn: ApplyFontsFn }
  | { readonly type: "RegisterOnSettingsSaved"; readonly fn: OnSettingsSavedFn }
  | {
      readonly type: "OpenSettings";
      readonly section: Maybe<ProjectSettingsSectionId>;
    }
  | {
      readonly type: "UpdateManuscript";
      readonly config: Maybe<ZwConfig>;
      readonly manuscript: ManuscriptItem[];
    }
  | {
      readonly type: "UpdateChapterScenes";
      readonly config: Maybe<ZwConfig>;
      readonly folder: string;
      readonly scenes: ZwScene[];
    }
  | {
      readonly type: "UpdateSettings";
      readonly config: Maybe<ZwConfig>;
      readonly payload: ProjectSettingsSavePayload;
    }
  | {
      readonly type: "AddDatabaseField";
      readonly config: Maybe<ZwConfig>;
      readonly def: ZwDatabaseFieldDef;
    }
  | {
      readonly type: "AddDatabaseRelationPair";
      readonly config: Maybe<ZwConfig>;
      readonly pair: ZwDatabaseRelationPair;
    };

/** What to do after a settings write succeeds. */
export type SettingsSavedPlan = {
  readonly notify: Maybe<OnSettingsSavedFn>;
  /** True when a session goal was just introduced (reset the session clock). */
  readonly resetSession: boolean;
};

export type ProjectSettingsCmd =
  | {
      readonly kind: "openSettings";
      readonly fn: OpenSettingsFn;
      readonly section: Maybe<ProjectSettingsSectionId>;
    }
  | {
      readonly kind: "applyFonts";
      readonly fn: ApplyFontsFn;
      readonly fonts: ZwFonts;
    }
  | {
      /** Optimistic write with rollback; `saved` runs only after success. */
      readonly kind: "writeConfig";
      readonly next: ZwConfig;
      readonly label: string;
      readonly saved: Maybe<SettingsSavedPlan>;
    };

function writeOnly(
  model: ProjectSettingsModel,
  next: ZwConfig,
  label: string,
): readonly [ProjectSettingsModel, readonly ProjectSettingsCmd[]] {
  return [model, [{ kind: "writeConfig", next, label, saved: nothing }]];
}

export function updateProjectSettings(
  model: ProjectSettingsModel,
  msg: ProjectSettingsMsg,
): readonly [ProjectSettingsModel, readonly ProjectSettingsCmd[]] {
  switch (msg.type) {
    case "RegisterOpenSettings":
      return [{ ...model, openSettings: just(msg.fn) }, []];
    case "RegisterApplyFonts":
      return [{ ...model, applyFonts: just(msg.fn) }, []];
    case "RegisterOnSettingsSaved":
      return [{ ...model, onSettingsSaved: just(msg.fn) }, []];
    case "OpenSettings": {
      if (isNothing(model.openSettings)) return [model, []];
      return [
        model,
        [
          {
            kind: "openSettings",
            fn: model.openSettings.value,
            section: msg.section,
          },
        ],
      ];
    }
    case "UpdateManuscript": {
      if (isNothing(msg.config)) return [model, []];
      return writeOnly(
        model,
        { ...msg.config.value, manuscript: msg.manuscript },
        "Failed to update the manuscript order",
      );
    }
    case "UpdateChapterScenes": {
      if (isNothing(msg.config)) return [model, []];
      const config = msg.config.value;
      const chapter = toMaybe(
        flattenChapters(config.manuscript).find((c) => c.folder === msg.folder),
      );
      if (isNothing(chapter)) return [model, []];
      return writeOnly(
        model,
        {
          ...config,
          manuscript: replaceChapter(config.manuscript, msg.folder, {
            ...chapter.value,
            scenes: msg.scenes,
          }),
        },
        "Failed to update the manuscript order",
      );
    }
    case "UpdateSettings": {
      if (isNothing(msg.config)) return [model, []];
      const config = msg.config.value;
      const { payload } = msg;
      const previousGoals = config.goals ?? defaultZwGoals();
      const next: ZwConfig = {
        ...config,
        fonts: payload.fonts,
        printSize: payload.printSize,
        paragraphFormatting: payload.paragraphFormatting,
        chapterHeadingStyle: payload.chapterHeadingStyle,
        chapterNumbering: payload.chapterNumbering,
        actNumbering: payload.actNumbering,
        pageChrome: payload.pageChrome,
        sceneFormatting: payload.sceneFormatting,
        copyrightPage: payload.copyrightPage,
        dedicationPage: payload.dedicationPage,
        metadata: payload.metadata,
        dictionary: payload.dictionary,
        statusBar: payload.statusBar,
        goals: normalizeZwGoals(payload.goals),
        databaseFields: payload.databaseFields,
        databaseRelationPairs: payload.databaseRelationPairs,
      };
      const hadSession = isJust(
        goalTarget(toMaybe(previousGoals.sessionWords)),
      );
      const hasSession = isJust(
        goalTarget(toMaybe(payload.goals.sessionWords)),
      );
      const fontsCmds: readonly ProjectSettingsCmd[] = isJust(model.applyFonts)
        ? [
            {
              kind: "applyFonts",
              fn: model.applyFonts.value,
              fonts: payload.fonts,
            },
          ]
        : [];
      return [
        model,
        [
          ...fontsCmds,
          {
            kind: "writeConfig",
            next,
            label: "Failed to update project settings",
            saved: just({
              notify: model.onSettingsSaved,
              resetSession: hasSession && hadSession === false,
            }),
          },
        ],
      ];
    }
    case "AddDatabaseField": {
      if (isNothing(msg.config)) return [model, []];
      return writeOnly(
        model,
        {
          ...msg.config.value,
          databaseFields: [...msg.config.value.databaseFields, msg.def],
        },
        "Failed to add worldbuilding field",
      );
    }
    case "AddDatabaseRelationPair": {
      if (isNothing(msg.config)) return [model, []];
      return writeOnly(
        model,
        {
          ...msg.config.value,
          databaseRelationPairs: [
            ...(msg.config.value.databaseRelationPairs ?? []),
            msg.pair,
          ],
        },
        "Failed to add relationship pair",
      );
    }
    default:
      return assertNever(msg);
  }
}
